import { useRef } from 'react';
import { FileIcon, UploadIcon, XIcon } from 'lucide-react';
import { Button } from '@/components/Button';
import type { StagedFile } from '@/hooks/useStagedAttachments';

type UploadAccent = 'indigo' | 'violet';

const TRIGGER_ACCENT: Record<UploadAccent, string> = {
  indigo: 'hover:border-indigo-300 hover:text-indigo-600',
  violet: 'hover:border-violet-300 hover:text-violet-600',
};

interface TaskAttachmentUploadAreaProps {
  staged: StagedFile[];
  onAdd: (files: FileList) => void;
  onRemove: (localId: string) => void;
  /** Shown next to a staged file that failed to upload. */
  failedLabel: string;
  /**
   * Employee portal shows an inline "Attach files" trigger button so users can
   * add more files directly from this widget. Company portal's only trigger is
   * an external button elsewhere in the compose row, so it omits this one.
   * Required only when showTriggerButton is true.
   */
  attachLabel?: string;
  showTriggerButton?: boolean;
  accent?: UploadAccent;
}

export function TaskAttachmentUploadArea({
  staged,
  onAdd,
  onRemove,
  failedLabel,
  attachLabel,
  showTriggerButton = false,
  accent = 'indigo',
}: TaskAttachmentUploadAreaProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {showTriggerButton && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className={`inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-500 transition-colors ${TRIGGER_ACCENT[accent]}`}
          >
            <UploadIcon size={12} />
            {attachLabel}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => e.target.files && onAdd(e.target.files)}
        />
      </div>

      {staged.length > 0 && (
        <div className="space-y-1.5">
          {staged.map((f) => (
            <div
              key={f.localId}
              className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
            >
              <FileIcon size={12} className="shrink-0 text-gray-400" />
              <span className="flex-1 truncate text-xs text-gray-700">
                {f.file.name}
              </span>
              {f.uploading && (
                <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-indigo-400 border-t-transparent" />
              )}
              {f.error && (
                <span className="text-[10px] text-red-500">
                  {failedLabel}
                </span>
              )}
              <Button
                size="xs"
                type="button"
                variant="ghost"
                onClick={() => onRemove(f.localId)}
                className="text-gray-400 hover:text-red-500"
              >
                <XIcon size={16} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
